/**
 * Focused "your AI provider" modal — the surface the chat `/model` picker's
 * "Add or manage your own models…" row opens.
 *
 * It renders JUST the custom ("bring your own AI") provider manager
 * ({@link AiProviderManager}), not the full Synthase settings modal. The
 * picker's intent is narrow — add/manage the models it lists.
 *
 * Scope is the user's ACCOUNT (`/account/ai-provider`), not the project: a
 * personal endpoint (a local model, your own key) should be available across
 * every one of your projects and show up in the account settings panel — not be
 * trapped in one project. Chat resolution already merges the project owner's
 * account providers into every project's catalog, so an account model is
 * selectable in the `/model` picker too.
 *
 * PROJECT-scoped rows (created by an earlier project-settings surface, or via
 * the admin-gated `/projects/:id/ai-provider` API) override same-named account
 * rows in the merged catalog. When any exist and the caller can see them
 * (admin+), they're listed in their own section above the account list, and the
 * shadowed account rows carry a warning — otherwise an account edit looks
 * silently ignored while the picker keeps serving the project row's config.
 *
 * Each provider row's "Use" button activates that provider's model as THIS
 * project's chat model (`custom/<provider>/<model>`) and signals the host so the
 * chat picker reflects it live. A save/delete drops the cached models catalog
 * and remounts the refetcher so the picker reflects the change on its next open.
 *
 * @module
 */

import { type JSX, useEffect, useState } from 'react'

import { resetAIModelsCache, useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Modal } from '@molecule/app-ui-react'

import { AiProviderManager, ModelsCatalogRefetch } from './AiProviderManager.js'

/** Props for {@link ModelProviderModal}. */
export interface ModelProviderModalProps {
  open: boolean
  onClose: () => void
  projectId: string
  /**
   * Whether the caller may change THIS project's chat model. A read-only viewer
   * cannot (the server 403s the write), so the per-row "Use" button is hidden
   * for them — they can still add/manage their own account providers. Defaults
   * to true when omitted.
   */
  canEdit?: boolean
  /**
   * The mode a "Use" should target — the conversation mode the user was in when
   * they opened the modal (discovery = plan). Set, "Use" persists the model to
   * that mode's setting (`planModel` / `executeModel`); unset, it falls back to
   * the legacy both-modes `chatModel`.
   */
  activeMode?: 'plan' | 'execute'
  /**
   * Called after "Use" sets a model as the project's chat model, so the host
   * can refresh the chat picker's live model state (it reads settings once on
   * mount otherwise).
   */
  onModelSelected?: () => void
}

/**
 * Renders the focused model-provider modal.
 *
 * @param props - Component props.
 * @param props.open - Whether the modal is open.
 * @param props.onClose - Called when the modal should close.
 * @param props.projectId - The project whose chat model "Use" sets.
 * @param props.onModelSelected - Called after "Use" sets the chat model.
 * @returns The rendered modal, or `null` when closed.
 */
export function ModelProviderModal({
  open,
  onClose,
  projectId,
  canEdit = true,
  activeMode,
  onModelSelected,
}: ModelProviderModalProps): JSX.Element | null {
  const { t } = useTranslation()
  const http = useHttpClient()
  const cm = getClassMap()

  // Bumped after a provider save/delete: drops the cached models catalog and
  // remounts the refetcher below (via its key) so the pickers pick up the
  // change. Mirrors SettingsModal's handleProvidersChanged.
  const [modelsEpoch, setModelsEpoch] = useState(0)

  // PROJECT-scoped provider names. A project row overrides a same-named account
  // row in this project's model catalog (whole-provider, by design), so hiding
  // project rows here made an account-level edit look ignored: the picker kept
  // serving the shadowing project row's old URL + models with nothing in the UI
  // saying why (observed in prod 2026-08-27, after the project-settings surface
  // that used to manage these rows was retired). `null` until probed; the probe
  // 403s for non-admins (the rows are admin-gated), which simply hides the
  // project section — only admins can have created project rows anyway.
  const [projectProviderNames, setProjectProviderNames] = useState<string[] | null>(null)
  const [probeTick, setProbeTick] = useState(0)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    void http
      .get<{ providers?: { name: string }[] }>(`/projects/${projectId}/ai-provider`)
      .then((res) => {
        if (!cancelled) setProjectProviderNames((res.data.providers ?? []).map((p) => p.name))
      })
      .catch(() => {
        // Not an admin (403) / guest (401) / transient failure — treat as "no
        // project rows visible" and show only the account section, as before.
        if (!cancelled) setProjectProviderNames(null)
      })
    return () => {
      cancelled = true
    }
  }, [http, projectId, open, probeTick])

  const handleProvidersChanged = (): void => {
    resetAIModelsCache()
    setModelsEpoch((n) => n + 1)
    // Re-probe so the shadow warnings + the project section reflect the write
    // (deleting the last project row removes the section entirely).
    setProbeTick((n) => n + 1)
  }

  // "Use": persist the model for the mode the user is in (plan while
  // planning/discovering, execute while building — activeMode from the host;
  // legacy chatModel only when no mode context arrived), tell the host to
  // refresh its live picker, and close. Leaves the modal open on failure so the
  // user can retry rather than believing it worked.
  const handleUseModel = (customModelId: string): void => {
    const settingKey = activeMode ? `${activeMode}Model` : 'chatModel'
    void http
      .patch(`/projects/${projectId}`, { settings: { [settingKey]: customModelId } })
      .then(() => {
        onModelSelected?.()
        onClose()
      })
      .catch((error: unknown) => {
        console.error('Failed to set chat model from provider modal', error)
      })
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t('workspace.settings.aiProvider.title', undefined, {
        defaultValue: 'Your AI provider',
      })}
      data-mol-id="model-provider-modal"
    >
      {/* Project-scoped providers first (they win over same-named account
          rows in this project's catalog) — shown only when rows exist, so the
          admin who once created them can still see, edit, and delete them now
          that the old project-settings surface is gone. */}
      {projectProviderNames != null && projectProviderNames.length > 0 && (
        <div data-mol-id="model-provider-project-section">
          <h4 className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'))}>
            {t('workspace.settings.aiProvider.projectHeading', undefined, {
              defaultValue: 'This project’s providers',
            })}
          </h4>
          <AiProviderManager
            basePath={`/projects/${projectId}/ai-provider`}
            molIdPrefix="model-project-ai-provider"
            onChanged={handleProvidersChanged}
            // The probe succeeded, so the caller is a project admin — admins can
            // always set the project's model.
            onUseModel={handleUseModel}
          />
          <h4 className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'), cm.sp('mt', 4))}>
            {t('workspace.settings.aiProvider.accountHeading', undefined, {
              defaultValue: 'Your account providers (all your projects)',
            })}
          </h4>
        </div>
      )}
      <AiProviderManager
        basePath="/account/ai-provider"
        onChanged={handleProvidersChanged}
        // A viewer can manage their own account providers but cannot set THIS
        // project's model — omitting onUseModel hides the per-row "Use" button.
        onUseModel={canEdit ? handleUseModel : undefined}
        overriddenNames={projectProviderNames ?? undefined}
      />
      <ModelsCatalogRefetch key={modelsEpoch} projectId={projectId} />
    </Modal>
  )
}
